import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { formatPhone } from '../lib/phone';
import { apiFetch } from '../api';
import {
  PageHeader, Loading, ErrorBanner, Empty, Badge, StatCard, ui,
} from '../components/adminUi';
import Icon from '../components/Icon';

// FAZA 7 — moliyaviy hisobot, kredit DAFTARIGA asoslangan.
//
// MUHIM tushuncha (sahifada ham izohlanadi): kredit OLDINDAN to'lanadi.
// To'ldirish — bu hali daromad EMAS, u ustaning hisobidagi pul (majburiyat);
// daromad kredit SARFLANGANDA tan olinadi — kelishuv ulushi yoki Prime obunasi.
export default function ReportsPage() {
  const { auth } = useAuth();

  const [period, setPeriod] = useState('daily');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const res = await apiFetch(`/api/admin/reports/money?period=${period}`, auth.token);
      if (!res.ok) throw new Error();
      setData(await res.json());
    } catch {
      setData(null);
      setFetchError('Hisobotni yuklashda xatolik. Sahifani yangilang.');
    } finally {
      setLoading(false);
    }
  }, [auth.token, period]);

  useEffect(() => { load(); }, [load]);

  const buckets = data?.buckets ?? [];
  const t = data?.totals ?? null;
  const maxTotal = Math.max(1, ...buckets.map((b) => b.total || 0));

  return (
    <AdminLayout>
      <PageHeader title="Moliyaviy hisobot" subtitle="TezHal daromadi, kredit aylanmasi va majburiyatlar">
        <div style={s.periodRow}>
          {PERIODS.map((p) => (
            <button
              key={p.key}
              style={{ ...s.periodBtn, ...(period === p.key ? s.periodBtnActive : {}) }}
              onClick={() => setPeriod(p.key)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <button style={ui.pagerBtn} onClick={load}><Icon name="refresh" size={14} style={{ marginRight: '6px' }} />Yangilash</button>
      </PageHeader>

      <ErrorBanner>{fetchError}</ErrorBanner>

      {loading ? (
        <Loading />
      ) : !t ? (
        <Empty icon="barChart" title="Ma'lumot yo'q" hint="Hisobot shakllanishi uchun bitim yoki obuna kerak." />
      ) : (
        <>
          <h2 style={s.section}>Daromad</h2>
          <div className="th-stat-grid-3" style={ui.statGrid3}>
            <StatCard label="TezHal daromadi" value={money(t.revenue)} highlight />
            <StatCard label={`Kelishuv komissiyalari (${t.dealFees.n} ta)`} value={money(t.dealFees.sum)} />
            <StatCard label={`Prime obunalari (${t.prime.n} ta)`} value={money(t.prime.sum)} />
          </div>

          <h2 style={s.section}>Kredit aylanmasi</h2>
          <p style={s.hint}>
            Kredit <b>oldindan</b> to'lanadi: to'ldirish hali daromad emas — u ustaning hisobidagi
            pul (<b>majburiyat</b>). Daromad kredit sarflanganda tan olinadi.
          </p>
          <div className="th-stat-grid-3" style={ui.statGrid3}>
            <StatCard label={`To'ldirishlar (${t.topups.n} ta)`} value={money(t.topups.sum)} />
            <StatCard label="Ustalar qo'lidagi kredit" value={money(t.liability)} />
            <StatCard label="Berilgan bonuslar" value={money(t.bonuses)} />
          </div>

          <h2 style={s.section}>E'tibor talab qiladi</h2>
          <div className="th-stat-grid-3" style={ui.statGrid3}>
            <StatCard
              label={`Undirilmagan ulush (${t.pendingFees.n} ta)`}
              value={money(t.pendingFees.sum)}
              accent={t.pendingFees.sum > 0 ? 'var(--color-danger)' : 'var(--color-text)'}
            />
            <StatCard
              label={`Manfiy balanslar`}
              value={money(t.debt)}
              accent={t.debt > 0 ? 'var(--color-danger)' : 'var(--color-text)'}
            />
            <StatCard
              label={`Prime tufayli olinmadi (${t.waivedFees.n} ta)`}
              value={money(t.waivedFees.sum)}
            />
          </div>

          <h2 style={s.section}>Platforma aylanmasi</h2>
          <p style={s.hint}>
            Yakunlangan bitimlarning umumiy summasi — bu TezHal daromadi <b>emas</b>, mijoz
            ustaga to'laydigan pul. Platforma hajmini ko'rsatadi.
          </p>
          <div className="th-stat-grid-3" style={ui.statGrid3}>
            <StatCard label={`Bitimlar hajmi (${t.gmv.n} ta)`} value={money(t.gmv.sum)} />
          </div>

          {buckets.length === 0 ? (
            <Empty icon="barChart" title="Bu davr uchun daromad yo'q" hint="Bitim yoki Prime obunasi paydo bo'lgach shakllanadi." />
          ) : (
            <>
              <div style={s.chartCard}>
                <div style={s.chartTitle}>Daromad dinamikasi</div>
                <div style={s.chartFrame}>
                  <div style={s.chartGrid} aria-hidden="true" />
                  <div style={s.chart}>
                    {[...buckets].reverse().map((b, i) => (
                      <div key={i} style={s.barCol} title={`${b.period}: ${money(b.total)} so'm`}>
                        <div style={{ ...s.bar, height: `${Math.max(4, Math.round((b.total / maxTotal) * 140))}px` }} />
                        <span style={s.barLabel}>{shortLabel(b.period)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div style={ui.tableWrap}>
                <table style={ui.table}>
                  <thead>
                    <tr>
                      <th style={ui.th}>Davr</th>
                      <th style={{ ...ui.th, textAlign: 'right' }}>Kelishuv komissiyalari</th>
                      <th style={{ ...ui.th, textAlign: 'right' }}>Prime</th>
                      <th style={{ ...ui.th, textAlign: 'right' }}>Jami</th>
                      <th style={{ ...ui.th, textAlign: 'right' }}>Bitimlar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {buckets.map((b, i) => (
                      <tr key={i} style={ui.tr}>
                        <td style={{ ...ui.td, fontWeight: '600', color: 'var(--color-text)' }}>{b.period}</td>
                        <td style={{ ...ui.td, textAlign: 'right' }}>{money(b.dealFees)}</td>
                        <td style={{ ...ui.td, textAlign: 'right' }}>{money(b.prime)}</td>
                        <td style={{ ...ui.td, textAlign: 'right', fontWeight: '700', color: 'var(--color-text)' }}>
                          {money(b.total)}
                        </td>
                        <td style={{ ...ui.td, textAlign: 'right' }}>{b.deals}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {data.topProviders.length > 0 && (
            <>
              <h2 style={{ ...s.section, marginTop: '24px' }}>Eng ko'p ulush to'lagan ustalar</h2>
              <div style={ui.tableWrap}>
                <table style={ui.table}>
                  <thead>
                    <tr>
                      <th style={ui.th}>Usta</th>
                      <th style={ui.th}>Telefon</th>
                      <th style={{ ...ui.th, width: '130px' }}>Prime</th>
                      <th style={{ ...ui.th, width: '120px', textAlign: 'right' }}>Bitimlar</th>
                      <th style={{ ...ui.th, width: '160px', textAlign: 'right' }}>To'langan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.topProviders.map((p) => (
                      <tr key={p.id} style={ui.tr}>
                        <td style={{ ...ui.td, fontWeight: '600', color: 'var(--color-text)' }}>
                          {[p.firstName, p.lastName].filter(Boolean).join(' ') || `#${p.id}`}
                        </td>
                        <td style={ui.td}>{formatPhone(p.phone) || '—'}</td>
                        <td style={ui.td}>
                          {p.primeActive
                            ? <Badge tone="amber"><Icon name="star" size={12} style={{ marginRight: '6px' }} />Faol</Badge>
                            : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                        </td>
                        <td style={{ ...ui.td, textAlign: 'right' }}>{p.deals}</td>
                        <td style={{ ...ui.td, textAlign: 'right', fontWeight: '700', color: 'var(--color-text)' }}>
                          {money(p.paid)} so'm
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </AdminLayout>
  );
}

const PERIODS = [
  { key: 'daily', label: 'Kunlik' },
  { key: 'weekly', label: 'Haftalik' },
  { key: 'monthly', label: 'Oylik' },
];

// Bo'sh joyli format: 1 000 000
function money(v) {
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return '0';
  const sign = n < 0 ? '−' : '';
  return sign + Math.round(Math.abs(n)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
}

function shortLabel(l) {
  const str = String(l || '');
  return str.length > 6 ? str.slice(5) : str;
}

const s = {
  section: { margin: '24px 0 12px', fontSize: '18px', fontWeight: '700', color: 'var(--color-text)' },
  hint: { margin: '-6px 0 14px', fontSize: '13px', color: 'var(--color-text-muted)', maxWidth: '760px' },
  periodRow: {
    display: 'flex', gap: '4px', background: 'var(--color-surface-alt)', padding: '4px',
    borderRadius: 'var(--radius-btn)',
  },
  periodBtn: {
    padding: '8px 18px', background: 'transparent', color: 'var(--color-text-muted)',
    border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', fontWeight: '500',
  },
  periodBtnActive: { background: 'var(--color-primary)', color: 'var(--color-on-primary)', fontWeight: '700' },
  chartCard: {
    background: 'var(--color-surface-alt)', borderRadius: 'var(--radius-card)', padding: '16px',
    margin: '24px 0',
  },
  chartTitle: { fontSize: '14px', fontWeight: '700', color: 'var(--color-text)', marginBottom: '16px' },
  chartFrame: { position: 'relative', height: '170px' },
  // gorizontal to'r chiziqlari (#E6E6E6) + pastki o'q chizig'i; ustunlar maydoni = yuqori 144px
  chartGrid: {
    position: 'absolute', left: 0, right: 0, top: 0, height: '145px', pointerEvents: 'none',
    backgroundImage: 'repeating-linear-gradient(to bottom, var(--color-border) 0, var(--color-border) 1px, transparent 1px, transparent 36px)',
    borderBottom: '1px solid var(--color-text-muted)',
  },
  chart: {
    position: 'relative', display: 'flex', alignItems: 'flex-end', gap: '10px', height: '100%',
    overflowX: 'auto',
  },
  barCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', minWidth: '38px', height: '100%' },
  bar: { width: '28px', background: 'var(--color-primary)', borderRadius: '6px 6px 0 0' },
  barLabel: { fontSize: '12px', lineHeight: '20px', height: '20px', color: 'var(--color-text-muted)', whiteSpace: 'nowrap' },
};
